import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Loader2, ZoomIn } from "lucide-react";

async function getCroppedBlob(src, area, outW, outH) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

// Crop + zoom dialog. Defaults to a round 512x512 avatar; pass aspect/outW/outH for other shapes.
export const CropDialog = ({ src, open, onClose, onCropped, aspect = 1, round = true, outW = 512, outH = 512,
  title = "Adjust your photo", description = "Drag to reposition and zoom for the perfect fit.", saveLabel = "Save photo",
  onSkip, skipLabel, wide = false, minZoom = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onComplete = useCallback((_area, px) => setAreaPixels(px), []);

  const run = async (fn) => {
    setSaving(true);
    try { await fn(); } finally { setSaving(false); }
  };
  const confirm = () => areaPixels && run(async () => onCropped(await getCroppedBlob(src, areaPixels, outW, outH)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className={`${wide ? "sm:max-w-3xl" : "sm:max-w-md"} bg-white`} data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{title}</DialogTitle>
          <DialogDescription className="text-slate-500">{description}</DialogDescription>
        </DialogHeader>
        <div className={`relative w-full ${wide ? "h-72" : "h-64"} bg-slate-900 rounded-xl overflow-hidden`}>
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} minZoom={minZoom} restrictPosition={minZoom >= 1}
              cropShape={round ? "round" : "rect"} showGrid={!round}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
          )}
        </div>
        <div className="flex items-center gap-3 py-1">
          <ZoomIn size={18} className="text-slate-400 shrink-0" />
          <Slider value={[zoom]} min={minZoom} max={3} step={0.01} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom-slider" className="flex-1" />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="crop-cancel-btn" className="flex-1 rounded-xl">Cancel</Button>
          {onSkip && <Button variant="outline" onClick={() => run(onSkip)} disabled={saving} data-testid="crop-skip-btn" className="flex-1 rounded-xl">{skipLabel}</Button>}
          <Button onClick={confirm} disabled={saving} data-testid="crop-save-btn" className="flex-1 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
            {saving ? <Loader2 className="animate-spin" size={18} /> : null} {saveLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
